import { all, equals, zip } from "ramda"

import type { Product } from "@encoway/c-services-js-client"
import { L10n } from "@encoway/l10n"

import { useAppDispatch, useAppSelector } from "../../app/hooks"
import {
    hasVisualizationChanged,
    selectVisualization
} from "../../app/visualizationSlice"
import {
    CABLE_COLOR_CONNECTORS,
    CABLE_CONNECTION_SIDE_1,
    CABLE_CONNECTION_SIDE_2,
    CABLE_LENGTH_CHARACTERISTIC,
    CABLE_MANUFACTURER_CHARACTERISTIC,
    CABLE_PRODUCT_IMAGE_CHARACTERISTIC,
    CABLE_SERIES,
    CABLE_TYPE,
    CABLE_X_POLE
} from "../../constants/cableConstants"
import { TranslationConstants } from "../../constants/translationConstants"
import { getCharacteristicValue } from "../../utils/characteristicValueService"
import {
    BomSliceState,
    ResultBomProduct,
    selectCableProducts,
    selectResultBom,
    setCableProducts,
    setResultBom
} from "./bomSlice"
import { determineCableProducts } from "./determineCableProductsService"
import { buildStructuredBom } from "./structuredBomService"

function toLongestCable(node: any) {
    return function (acc: Product, cur: Product) {
        if (!acc) {
            return cur
        }

        const cableLength = getCharacteristicValue(cur, "cable_length")
        const accCableLength = getCharacteristicValue(acc, "cable_length")
        if (isMatchingCableType(cur, node) && cableLength >= accCableLength) {
            return cur
        }

        return acc
    }
}

function isMatchingCableType(product: Product, node: any): boolean {
    const color = getCharacteristicValue(product, CABLE_COLOR_CONNECTORS)
    const connectionSide1 = getCharacteristicValue(
        product,
        CABLE_CONNECTION_SIDE_1
    )
    const connectionSide2 = getCharacteristicValue(
        product,
        CABLE_CONNECTION_SIDE_2
    )
    const xPole = getCharacteristicValue(product, CABLE_X_POLE)
    const series = getCharacteristicValue(product, CABLE_SERIES)

    const nodeConnectionSide1 = node.state.connection_side_1
    const nodeConnectionSide2 = node.state.connection_side_2

    const nodeSeries = node.state.series_to_connect
    const nodeXPole = String(node.state.x_pole)

    const nodeColor = node.state.colour_connectors

    const cableProperties = [
        color,
        connectionSide1,
        connectionSide2,
        series,
        xPole
    ]
    const nodeProperties = [
        nodeColor,
        nodeConnectionSide1,
        nodeConnectionSide2,
        nodeSeries,
        nodeXPole
    ]

    const matches = all(
        ([a, b]) => equals(a, b),
        zip(cableProperties, nodeProperties)
    )

    return matches
}

let cableCount = 0

export const useDetermineResultBom = () => {
    const visualizationChanged = useAppSelector(hasVisualizationChanged)
    const visualization = useAppSelector(selectVisualization)
    const resultBom = useAppSelector(selectResultBom)
    const cables = useAppSelector(selectCableProducts)
    const dispatch = useAppDispatch()

    const determineResultBom = async () => {
        if (visualizationChanged && visualization) {
            const structuredBom = await buildStructuredBom(visualization)
            let cableProducts = cables
            if (!cables.size) {
                cableProducts = await determineCableProducts()
                dispatch(setCableProducts(cableProducts))
            }
            const generatedResultBom = buildResultBom(
                structuredBom,
                cableProducts
            )
            dispatch(setResultBom(generatedResultBom))
            return generatedResultBom
        }
        return Promise.resolve(resultBom)
    }

    return { determineResultBom }
}

const buildResultBom = (
    structuredBom: object[],
    cables: Map<string, Map<string, Product>>
) => {
    const generatedResultBom: ResultBomProduct[] = []
    cableCount = 0
    structuredBom.forEach((node) => {
        const cableColor = getCharacteristicValue(
            // @ts-ignore
            node.nodeProperties,
            CABLE_TYPE
        ) as string

        if (cableColor) {
            cableCount += 1
            generatedResultBom.push(...splitCable(cables, cableColor, node))
        } else {
            generatedResultBom.push(createResultBomProduct(node))
        }
    })
    return generatedResultBom
}

const splitCable = (
    cables: BomSliceState["cableProducts"],
    cableColor: string,
    node: any
) => {
    const resultBomProducts: ResultBomProduct[] = []
    const coloredCableProducts =
        cables.get(cableColor) || new Map<number, Product>()

    let calculatedCableLength = getCableLength(node, true)

    const coloredCables = Array.from(coloredCableProducts.values())
    const longestCableProduct: Product = coloredCables.reduce(
        toLongestCable(node)
    )
    const longestCableLength = parseFloat(
        getCharacteristicValue(longestCableProduct, CABLE_LENGTH_CHARACTERISTIC)
    )
    if (calculatedCableLength > longestCableLength) {
        calculatedCableLength = addBomProductsForLongestCable(
            resultBomProducts,
            calculatedCableLength,
            longestCableLength,
            longestCableProduct,
            node
        )
    }
    for (const cable of coloredCables) {
        const cableLength = parseFloat(
            getCharacteristicValue(cable, CABLE_LENGTH_CHARACTERISTIC)
        )

        if (
            isMatchingCableType(cable, node) &&
            calculatedCableLength < cableLength
        ) {
            resultBomProducts.push(
                createResultBomProduct({
                    ...cable,
                    node
                })
            )
            calculatedCableLength -= cableLength
            break
        }
    }

    return resultBomProducts
}

const addBomProductsForLongestCable = (
    resultBomProducts: ResultBomProduct[],
    cableLength: number,
    longestCableLength: number,
    productToAdd: Product,
    node: any
) => {
    let calculatedCableLength = cableLength
    while (calculatedCableLength > longestCableLength) {
        resultBomProducts.push(
            createResultBomProduct({
                ...productToAdd,
                node
            })
        )
        calculatedCableLength -= longestCableLength
    }
    return calculatedCableLength
}

const createResultBomProduct = (node: any): ResultBomProduct => {
    if ("nodeProperties" in node) {
        return {
            id: node.nodeProperties.id,
            name: node.nodeProperties.name || "",
            quantity: node.quantity || "",
            productImage:
                getCharacteristicValue(
                    node.nodeProperties,
                    CABLE_PRODUCT_IMAGE_CHARACTERISTIC
                ) || "",
            room: node.state.room || "",
            manufacturer: node.state.manufacturer || "",
            calculatedLength: getCableLength(node) || "",
            cableLength: getCableLength(node)
        }
    } else {
        return {
            id: node.id,
            name: node.name || "",
            quantity: node.node.quantity || "",
            productImage:
                getCharacteristicValue(
                    node,
                    CABLE_PRODUCT_IMAGE_CHARACTERISTIC
                ) || "",
            room: node.node.state.room || "",
            manufacturer:
                getCharacteristicValue(
                    node,
                    CABLE_MANUFACTURER_CHARACTERISTIC
                ) || "",
            calculatedLength: getCableLength(node) || "",
            cableLength:
                getCharacteristicValue(node, CABLE_LENGTH_CHARACTERISTIC) || "",
            additionalInformation: `${L10n.format(
                TranslationConstants.General.CABLE
            )} ${cableCount}`
        }
    }
}

const getCableLength = (node: any, parseAsNumber?: boolean) => {
    const state = node.state || node.node?.state

    if (!state) {
        return parseAsNumber ? 0 : ""
    }

    const cableLength = state["/Kabellänge"] || state.cable_length || ""
    if (parseAsNumber) {
        return Number.parseFloat(cableLength)
    } else {
        return cableLength
    }
}
